// @file Board-specific AI chat surface strategy.
// Consumed by useAIChatSurfaceStrategy factory — use that composable, not this one directly.
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import type { WidgetType } from '@@/bits/surface_ai_chat_widgets'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import type { Suggestion } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { computed } from 'vue'
import type { AIChatPersonalizedTool, AIChatSurfaceStrategyReturn } from './useAIChatSurfaceStrategy'

function buildBoardFallbackSuggestions(): Suggestion[] {
  const createdAt = Date.now()
  return [
    {
      short_message: __('✨ What can you help me with?'),
      long_message: __('What can you help me with?'),
      created_at: createdAt,
    },
    {
      short_message: __('✏️ Add posts about a topic of my choosing.'),
      long_message: __('Add posts about a topic of my choosing.'),
      created_at: createdAt,
    },
    {
      short_message: __('📝 How do I add custom fields to my posts?'),
      long_message: __('How do I add custom fields to my posts?'),
      created_at: createdAt,
    },
  ]
}

function useBoardAIChatSurfaceStrategy(): AIChatSurfaceStrategyReturn {
  const surfaceStore = useSurfaceStore()
  const surfaceAccountsStore = useSurfaceAccountsStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const demoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()

  const surfaceType = 'board' as const

  const xAiChat = computed(() => {
    if (!demoPadletPanelStore.demoPadletPanelVisibility.showSurfaceAiChat) return false

    if (isAppUsing('surfaceAiChatSchools') && surfaceAccountsStore.isEducationalLibrary) {
      return surfaceAccountsStore.currentLibrary?.libraryAccess?.canUsePadletIntelligence || false
    }

    // For users that specifically enabled surface chat, we show it no matter the conditions.
    if (isAppUsing('surfaceAiChat')) {
      return true
    }

    if (!surfacePermissionsStore.amIOwner) return false
    if (!surfaceStore.isMagicWall) return false
    if (surfaceStore.magicWallType === 'learning_assessment_polls') return false

    // Else, we show it for library and backpack teachers.
    if (surfaceAccountsStore.isLibraryTeacherOrAbove || surfaceAccountsStore.isBackpackTeacherOrAbove) return true

    return false
  })

  const introduction = {
    title: __('Padlet Intelligence'),
    description: __('Generate new content, customize your padlet and much more through our chat interface.'),
    manualMessagePlaceholder: __('I want a QR code to share my padlet!'),
  }

  const fallbackSuggestions = buildBoardFallbackSuggestions()

  const shouldFetchSuggestionsFromApi = computed<boolean>(() => {
    return surfacePostsStore.currentPostsCount > 0
  })

  const personalizedTools = computed<AIChatPersonalizedTool[]>(() => {
    return [
      {
        widgetType: 'padlet_wallpaper_selector' as WidgetType,
        icon: 'picture_outline',
        text: __('Wallpaper generator'),
        message: __('Could you suggest some wallpapers for my padlet?'),
        show: surfacePermissionsStore.canUpdateWallSettings && !surfaceStore.isMap,
      },
      {
        widgetType: 'padlet_icon_selector' as WidgetType,
        icon: 'smiley',
        text: __('Icon suggester'),
        message: __('Show me some potential icons for my padlet.'),
        show: surfacePermissionsStore.canUpdateWallSettings,
      },
      {
        widgetType: 'padlet_font_selector' as WidgetType,
        icon: 'text',
        text: __('Font selector'),
        message: __('Is it possible to customize the padlet font?'),
        show: surfacePermissionsStore.canUpdateWallSettings,
      },
      {
        widgetType: 'export_padlet' as WidgetType,
        icon: 'document_pdf',
        text: __('PDF exporter'),
        message: __('I want to export this padlet as a PDF.'),
        show: surfacePermissionsStore.canIAccessContentDirectly,
        eventProps: {
          format: 'pdf',
        },
      },
      {
        widgetType: 'padlet_qr_code' as WidgetType,
        icon: 'qr',
        text: __('QR code'),
        message: __('May I get a QR code to share this padlet?'),
        show: surfacePermissionsStore.canIAccessContentDirectly,
      },
      {
        widgetType: 'export_padlet' as WidgetType,
        icon: 'document_text',
        text: __('CSV exporter'),
        message: __('I would like to get a CSV file with the contents of this padlet.'),
        show: surfacePermissionsStore.canIAccessContentDirectly,
        eventProps: {
          format: 'csv',
        },
      },
    ]
      .filter((tool) => tool.show)
      .map(({ show: _show, ...tool }) => tool)
  })

  return {
    surfaceType,
    xAiChat,
    introduction,
    fallbackSuggestions,
    shouldFetchSuggestionsFromApi,
    personalizedTools,
  }
}

export { useBoardAIChatSurfaceStrategy }
